import React, { useState, useMemo, useCallback, useRef } from "react";
import useHandleHoursByUserId from "../../Hooks/useHandleData/useHandleHoursByUserId";
import useHandleBillingById from "../../Hooks/useHandleData/useHandleBillingById";
import useHandleWorkCenter from "../../Hooks/useHandleData/useHandleWorkCenter";
import useHandleTimeOffById from "../../Hooks/useHandleData/useHandleTimeOffById";
import useHandleAllUsers from "../../Hooks/useHandleData/useHandleAllUsers";
import DefaultForm from "./components/form";
import SickLeaveForm from "./components/sickLeaveForm";
import RequestTimeOffForm from "./components/requestTimeOffForm";
import FormPopper from "./components/formPopper";
import { getUserId } from "../../Utils/helper";
import { Button } from "devextreme-react/button";
import { getUsersWorkCenter } from "../../Utils/helper";
import Popper from "@mui/material/Popper";
import "./styles.css";

const startTimeData = [
    { id: 7, display: "7:00 am" },
    { id: 8, display: "8:00 am" },
    { id: 9, display: "9:00 am" },
    { id: 10, display: "10:00 am" },
    { id: 11, display: "11:00 am" },
    { id: 12, display: "12:00 pm" },
    { id: 13, display: "1:00 pm" },
    { id: 14, display: "2:00 pm" },
    { id: 15, display: "3:00 pm" },
    { id: 16, display: "4:00 pm" },
    { id: 17, display: "5:00 pm" },
  ],
  endTimeData = [
    { id: 8, display: "8:00 am" },
    { id: 9, display: "9:00 am" },
    { id: 10, display: "10:00 am" },
    { id: 11, display: "11:00 am" },
    { id: 12, display: "12:00 pm" },
    { id: 13, display: "1:00 pm" },
    { id: 14, display: "2:00 pm" },
    { id: 15, display: "3:00 pm" },
    { id: 16, display: "4:00 pm" },
    { id: 17, display: "5:00 pm" },
    { id: 18, display: "6:00 pm" },
  ];

/*-=-=-=-=-=-=-=-=-=-=-=-= UploadHoursForm Component -=-=-=-=-=-=-=-=-=-=-=-=*/
const UploadHoursForm = ({ parentData, userId, onClose }) => {
  const isOtherUser = userId !== getUserId();
  const { getUsersWorkCenterById } = useHandleAllUsers();
  const defaultFormData = {
      locationId: isOtherUser
        ? getUsersWorkCenterById(userId) || getUsersWorkCenter()?.id
        : getUsersWorkCenter()?.id,
      taskId: null,
      billingAndWorkId: null,
      startDate: parentData?.startDate || new Date(),
      endDate: parentData?.startDate || new Date(),
      startTime: "8:00am",
      endTime: "5:00pm",
      totalHours: 0,
      description: "",
    },
    [formData, setFormData] = useState({
      ...defaultFormData,
      ...parentData,
    }),
    [loadVisible, setLoadVisible] = useState(false),
    formRef = useRef(null),
    hoursName =
      formData?.id !== null && formData?.id !== undefined
        ? "Update Hours"
        : "Upload Hours",
    [formPage, setFormPage] = useState(hoursName),
    [showPopper, setPopper] = useState(false),
    [anchorEl, setAnchorEl] = useState(null),
    [dateCheck, setDateCheck] = useState(parentData?.startDate || new Date());
  const [selectedLocation, setLocation] = useState(formData.locationId);
  const [selectedTask, setTask] = useState(formData.taskId);
  const { handlePostHours, handlePutHours } = useHandleHoursByUserId(userId);
  const { billingAndWorkArr, billingType } = useHandleBillingById(
    selectedLocation,
    selectedTask
  );
  const { allWorkCenterData } = useHandleWorkCenter();
  const { handlePostTimeOff } = useHandleTimeOffById();
  /*-=-=-=-=-=-=-=-=-=-=-=-= Editor Options -=-=-=-=-=-=-=-=-=-=-=-=*/
  // https://js.devexpress.com/React/Documentation/Guide/React_Components/Optimize_Performance/#Do_not_pass_inline_functions_and_objects_as_component_props
  // The article talks about creating the editor option objects outside the render funtion
  // or using useMemo. if its directly in the render, the dom will not know thats its the same object and rerender the item everytime
  const formInstance = useRef(null);
  const taskArr = useMemo(() => {
      if (allWorkCenterData) {
        const selectedWorkCenter = allWorkCenterData?.find(
          (x) => x.id === formData.locationId
        );
        return selectedWorkCenter?.tasks.map(({ id, name }) => ({ id, name }));
      }
    }, [allWorkCenterData, formData.locationId]),
    isSingleTask = useMemo(() => taskArr?.length === 1, [taskArr]),
    taskOptions = useMemo(
      () => ({
        items: taskArr,
        valueExpr: "id",
        displayExpr: "name",
        searchEnabled: true,
        placeholder: "Type or Select...",
      }),
      [taskArr]
    ),
    billingAndWorkOptions = useMemo(
      () => ({
        items: billingAndWorkArr,
        valueExpr: "id",
        displayExpr: "name",
        searchEnabled: true,
        placeholder: "Type or Select...",
      }),
      [billingAndWorkArr]
    );

  // this logic changes the name and tells the form which
  const onFormSwitch = useCallback((currentForm) => {
    setFormPage(currentForm);
  }, []);

  const formFieldDataChanged = useCallback(
    async (e) => {
      if (formPage === "Sick Leave" || formPage === "Request Time Off") {
        if (["startDate", "endDate", "startTime"].includes(e.dataField)) {
          // Trigger validation for the dependent field
          formRef.current.instance.validate();
        }
      }

      switch (e.dataField) {
        case "locationId": {
          setLocation(e.value);
          setTask(null);
          setFormData({
            ...formData,
            taskId: null,
            billingAndWorkId: null,
          });
          //console.log('locationId change taskArr', taskArr);
          break;
        }
        case "taskId": {
          setTask(e.value);
          break;
        }
        case "startDate": {
          const startDate = e.value;
          const oneDay = 24 * 60 * 60 * 1000;
          const dateCheck = new Date(startDate - oneDay);
          setDateCheck(dateCheck);
          break;
        }
        default: {
          //console.error('Error within the switch cases');
        }
      }
    },
    [formData]
  );

  /* =+=+=+= renders the sumbit button for loading animation =+=+=+= */

  const formOnClick = (event) => {
    setLoadVisible(true);
    const formValidation = formRef.current?.instance?.validate();

    if (
      formValidation.isValid &&
      (formPage === "Sick Leave" || formPage === "Request Time Off")
    ) {
      setPopper(true);
      setAnchorEl(event?.event?.currentTarget);
    } else if (formValidation.isValid) {
      onUploadData();
    } else {
      setLoadVisible(false);
    }
  };

  /*-=-=-=-=-=-=-=-=-=-=-=-= Backend Communication Logic-=-=-=-=-=-=-=-=-=-=-=-=*/
  const onUploadData = async () => {
    let isDataGood = false;

    switch (formPage) {
      case "Upload Hours": {
        isDataGood = await handlePostHours(
          formData,
          billingAndWorkArr,
          taskArr,
          billingType
        );

        break;
      }

      case "Update Hours": {
        isDataGood = await handlePutHours(
          formData,
          billingAndWorkArr,
          taskArr,
          billingType
        );

        break;
      }

      case "Sick Leave": {
        isDataGood = await handlePostTimeOff(formData, "Sick");

        break;
      }

      case "Request Time Off": {
        isDataGood = await handlePostTimeOff(formData, formData?.comment);

        break;
      }
      default:
        break;
    }

    if (isDataGood) {
      formInstance.current.reset();
      //formPage !== 'Upload Hours' && onClose();     //what is the purpose for this line
    }

    setLoadVisible(false);
  };

  const formProps = {
    formData,
    formRef,
    formFieldDataChanged,
    formPage,
    dateCheck,
    isSingleTask,
    taskOptions,
    billingAndWorkOptions,
    isOtherUser,
    formInstance,
  };

  return (
    <>
      <div id="upload-hours">
        <h1 className="white">{formPage}</h1>
        <div id="button-div">
          <button
            id="switch-form-button"
            hidden={formPage === hoursName}
            onClick={() => onFormSwitch(hoursName)}
          >
            {hoursName}
          </button>
          <button
            id="switch-form-button"
            hidden={
              isOtherUser ||
              formPage === "Sick Leave" ||
              hoursName === "Update Hours"
            }
            onClick={() => onFormSwitch("Sick Leave")}
          >
            Sick Leave
          </button>
          <button
            id="switch-form-button"
            hidden={
              isOtherUser ||
              formPage === "Request Time Off" ||
              hoursName === "Update Hours"
            }
            onClick={() => onFormSwitch("Request Time Off")}
          >
            Request Time Off
          </button>
        </div>
        {formPage === hoursName && <DefaultForm {...formProps} />}
        {formPage === "Sick Leave" && <SickLeaveForm {...formProps} />}
        {formPage === "Request Time Off" && (
          <RequestTimeOffForm {...formProps} />
        )}
        <div id="button-div">
          <Button
            id="button-div"
            className="submit-btn"
            text={formPage}
            onClick={formOnClick}
            disabled={loadVisible}
            width="250px"
            useSubmitBehavior={true}
            stylingMode="contained"
            type="default"
            height="50px"
          />
          <Popper
            id="popper-mui"
            open={showPopper}
            anchorEl={anchorEl}
            placement="top"
          >
            <FormPopper
              formData={formData}
              startTimeData={startTimeData}
              endTimeData={endTimeData}
              onUploadData={onUploadData}
              setLoadVisible={setLoadVisible}
              setPopper={setPopper}
            />
          </Popper>
        </div>
      </div>
    </>
  );
};

export default UploadHoursForm;
